import InputControl from '../../../components/InputControl/InputControl';
import InteractiveButton from '../../../components/InteractiveButton/InteractiveButton';
import { FormikConfig, useFormik } from 'formik';
import { IPreHeatRecipeEditable } from '../../../types/recipes-types';
import { fields } from '../constants';
import React, { useEffect, useMemo, useState } from 'react';
import Switcher, { ResponseSwitch } from '../../../components/Switcher/Switcher';
import CycleMeasurement from '../../../enums/cycle-measurement';
import CycleMeasurementSymbol from '../../../enums/cycle-measurement-symbol';
import { convertAbsoluteTemperature, convertTemperatureDelta } from '../../../utils/temperature-unit-conversion';
import { createPreHeatRecipeValidationSchema } from '../../../utils/recipe-unit-validation';

export interface PreHeatRecipeFormProps {
  initialValues: IPreHeatRecipeEditable;
  validationSchema: FormikConfig<IPreHeatRecipeEditable>['validationSchema'];
  onSubmit: (values: IPreHeatRecipeEditable) => any;
  formName: string;
  submitButtonName: string;
  enableTemperatureUnitSwitcher?: boolean;
}

const deltaFields: (keyof IPreHeatRecipeEditable)[] = ['rate', 'soakUpper', 'soakLower'];

const absoluteFields: (keyof IPreHeatRecipeEditable)[] = ['finalTemp'];

const convertPreHeatValues = (
  values: IPreHeatRecipeEditable,
  from: CycleMeasurement,
  to: CycleMeasurement,
): IPreHeatRecipeEditable => {
  const convertedValues = { ...values };

  deltaFields.forEach((key) => {
    (convertedValues as any)[key] = convertTemperatureDelta(convertedValues[key], from, to) as number;
  });

  absoluteFields.forEach((key) => {
    (convertedValues as any)[key] = convertAbsoluteTemperature(convertedValues[key], from, to) as number;
  });

  return convertedValues;
};

export const PreHeatRecipeForm: React.FC<PreHeatRecipeFormProps> = ({
  initialValues,
  validationSchema,
  onSubmit,
  formName,
  submitButtonName,
  enableTemperatureUnitSwitcher = false,
}) => {
  const [measurement, setMeasurement] = useState(CycleMeasurement.Celsius);
  const activeValidationSchema = useMemo(
    () => (enableTemperatureUnitSwitcher ? createPreHeatRecipeValidationSchema(measurement) : validationSchema),
    [enableTemperatureUnitSwitcher, measurement, validationSchema],
  );

  const formik = useFormik<IPreHeatRecipeEditable>({
    initialValues: initialValues,
    validationSchema: activeValidationSchema,
    onSubmit: (values) => {
      const celsiusValues = convertPreHeatValues(values, measurement, CycleMeasurement.Celsius);

      return onSubmit(celsiusValues);
    },
  });

  useEffect(() => {
    if (enableTemperatureUnitSwitcher) {
      formik.validateForm();
    }
  }, [enableTemperatureUnitSwitcher, measurement]);

  const onUnitsChange = (data: ResponseSwitch) => {
    const nextMeasurement = data.value ? CycleMeasurement.Celsius : CycleMeasurement.Fahrenheit;
    const convertedValues = convertPreHeatValues(formik.values, measurement, nextMeasurement);

    setMeasurement(nextMeasurement);
    formik.setValues(convertedValues, false);
  };

  const getFieldLabel = (item: (typeof fields)[number]) => {
    if (!enableTemperatureUnitSwitcher || item.key === 'name') {
      return item.label;
    }

    const symbol = CycleMeasurementSymbol[measurement];
    const suffix = item.key === 'rate' ? `${symbol}/h` : symbol;

    return `${item.label} (${suffix})`;
  };

  return (
    <div className="p-edit-controller pb-4 md:pb-25">
      <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <div className="flex items-center justify-between gap-4 border-b border-stroke px-7 py-4 dark:border-strokedark">
          <h3 className="font-medium text-black dark:text-white">{formName}</h3>
          {enableTemperatureUnitSwitcher && (
            <div className="c-input-control__input-wrapper">
              <Switcher
                options={{
                  left: CycleMeasurementSymbol.Celsius,
                  right: CycleMeasurementSymbol.Fahrenheit,
                }}
                isEnable={measurement === CycleMeasurement.Celsius}
                onChange={onUnitsChange}
                switcherId={`${initialValues.mode}-recipe-units-id`}
              />
            </div>
          )}
        </div>
        <form className="flex flex-col gap-5.5 p-7" onSubmit={formik.handleSubmit}>
          <div className="w-full">
            {fields.map((item) => {
              return (
                <InputControl
                  key={item.key}
                  label={getFieldLabel(item)}
                  type={item.inputType}
                  name={item.key}
                  placeholder={item.placeholder}
                  value={formik.values[item.key] ?? ''}
                  customOnChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  showError={formik.touched[item.key] && formik.errors[item.key]}
                  errorText={formik.errors[item.key]}
                />
              );
            })}
            <InteractiveButton text={submitButtonName} submit isLoading={formik.isSubmitting} />
          </div>
        </form>
      </div>
    </div>
  );
};
